import { Location } from '@angular/common';
import { Component, HostListener, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Subject, Subscription, firstValueFrom } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { AuthenticationContants } from 'src/app/constants/authentication-constants';
import { ProjectName } from 'src/app/constants/project-name-constants';
import { IvaTypeEnums } from 'src/app/enum/iva-type-enums';
import { ProviderInvoiceDetailSourceEnums } from 'src/app/enum/provider-invoice-detail-source-enums';
import { ProviderInvoiceStateEnum } from 'src/app/enum/provider-invoice-state-enum';
import { CustomListDropdown } from 'src/app/interfaces/custom-list/custom-list-dropdown.interface';
import { ArticleModel } from 'src/app/models/archive/article.model';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { ProviderInvoiceDetailBulkItemModel, ProviderInvoiceDetailBulkModel } from 'src/app/models/shopping/provider-invoice-detail-bulk.model';
import { ProviderInvoiceDetailModel } from 'src/app/models/shopping/provider-invoice-detail.model';
import { ProviderInvoiceModel } from 'src/app/models/shopping/provider-invoice.model';
import { ArticleOthersTableModel } from 'src/app/models/tables/shopping/article-others-table.model';
import { ArticleWithEntryTableModel } from 'src/app/models/tables/shopping/article-with-entry-table.model';
import { ArticleWithOCTableModel } from 'src/app/models/tables/shopping/article-with-oc-table.model';
import { CostCenterService } from 'src/app/services/archive/cost-center.service';
import { ProviderService } from 'src/app/services/archive/provider.service';
import { WindowService } from 'src/app/services/authentication/window.service';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DateConverterService } from 'src/app/services/global-services/date-converter.service';
import { FormControlStateService } from 'src/app/services/global-services/form-control-state.service';
import { HeaderService } from 'src/app/services/header/header.service';
import { BranchService } from 'src/app/services/management/branch.service';
import { EnterpriseService } from 'src/app/services/management/enterprise.service';
import { ProviderInvoiceDetailService } from 'src/app/services/shopping/provider-invoice-detail.service';
import { ProviderInvoiceService } from 'src/app/services/shopping/provider-invoice.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';
import { ListProviderInvoiceRetentionComponent } from '../../provider-invoice-retention/list-provider-invoice-retention/list-provider-invoice-retention.component';
import { PrefixInvoiceNumberComponent } from '../prefix-invoice-number/prefix-invoice-number.component';

const PROVIDER_INVOICE_STATE_CONFIG: Record<number, { label: string; severity: string; titleState: string; iconState: string }> = {
  [ProviderInvoiceStateEnum.open]: { label: 'Abierta', severity: 'success', titleState: 'Cerrar', iconState: 'pi pi-lock-open' },
  [ProviderInvoiceStateEnum.closed]: { label: 'Cerrada', severity: 'primary', titleState: 'Abrir', iconState: 'pi pi-lock' },
  [ProviderInvoiceStateEnum.printed]: { label: 'Impresa', severity: 'info', titleState: 'Abrir', iconState: 'pi pi-lock' },
  [ProviderInvoiceStateEnum.paid]: { label: 'Pagada', severity: 'warn', titleState: 'Abrir', iconState: 'pi pi-lock' },
};

@Component({
  selector: 'app-register-provider-invoice',
  templateUrl: './register-provider-invoice.component.html',
  styleUrls: ['./register-provider-invoice.component.scss'],
  standalone: false
})
export class RegisterProviderInvoiceComponent implements OnInit, OnDestroy {

  @ViewChild('articleOthersSelection') articleOthersSelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('articleWithEntrySelection') articleWithEntrySelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('articleWithOCSelection') articleWithOCSelectionModalComponent?: CustomModalComponent | null = null;
  @ViewChild('prefixInvoiceNumberModal') prefixInvoiceNumberModalComponent?: CustomModalComponent | null = null;
  @ViewChild('retentionsModal') retentionsModalComponent?: CustomModalComponent | null = null;

  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly branchService = inject(BranchService);
  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly costCenterService = inject(CostCenterService);
  private readonly dateConverterService = inject(DateConverterService);
  private readonly enterpriseService = inject(EnterpriseService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly formControlStateService = inject(FormControlStateService);
  private readonly headerService = inject(HeaderService);
  private readonly location = inject(Location);
  private readonly providerInvoiceDetailService = inject(ProviderInvoiceDetailService);
  private readonly providerInvoiceService = inject(ProviderInvoiceService);
  private readonly providerService = inject(ProviderService);
  private readonly router = inject(Router);
  private readonly title = inject(Title);
  private readonly toastHandlerService = inject(ToastHandlerService);
  private readonly windowService = inject(WindowService);

  activePanel: string = "";
  ascendantArticleOthers: boolean = true;
  ascendantArticleWithEntry: boolean = true;
  ascendantArticleWithOC: boolean = true;
  branchId = 0;
  codeCurrency: string = '';
  columnSortArticleOthers: string | null = null;
  columnSortArticleWithEntry: string | null = null;
  columnSortArticleWithOC: string | null = null;
  dataArticleOthers: PagedResponseModel<ArticleOthersTableModel> = new PagedResponseModel<ArticleOthersTableModel>();
  dataArticleWithEntry: PagedResponseModel<ArticleWithEntryTableModel> = new PagedResponseModel<ArticleWithEntryTableModel>();
  dataArticleWithOC: PagedResponseModel<ArticleWithOCTableModel> = new PagedResponseModel<ArticleWithOCTableModel>();
  disabledForm: boolean = false;
  filtersArticleOthers: ColumnResponseModel[] = [];
  filtersArticleWithEntry: ColumnResponseModel[] = [];
  filtersArticleWithOC: ColumnResponseModel[] = [];
  filteredEnterprises: any[] = [];
  formProviderInvoice: FormGroup = new FormGroup({});
  formProviderInvoiceFooter: FormGroup = new FormGroup({});
  headerArticleOthers: ColumnResponseModel[] = [];
  headerArticleWithEntry: ColumnResponseModel[] = [];
  headerArticleWithOC: ColumnResponseModel[] = [];
  iconState: string = "pi pi-lock-open";
  inputsForArticleOthersSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
  });
  inputsForArticleWithEntrySelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
  });
  inputsForArticleWithOCSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: true,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
  });
  inputsForRetentionsModal$ = new BehaviorSubject<any>({
    providerInvoiceModel: new ProviderInvoiceModel(),
    permissionUpdatable: false,
    disabledForm: false
  });
  isFormView: boolean = false;
  isOpened: string | undefined = undefined;
  limitArticleOthers: number = 10;
  limitArticleWithEntry: number = 10;
  limitArticleWithOC: number = 10;
  listBranch: any[] = [];
  listEnterprise: CustomListDropdown[] = [];
  listIvaType: CustomListDropdown[] = [];
  listProvider: any[] = [];
  nameWindow: string = 'Captura Facturas Proveedores';
  outputArticleOthers$ = new Subject<any>();
  outputArticleWithEntry$ = new Subject<any>();
  outputArticleWithOC$ = new Subject<any>();
  pageArticleOthers: number = 1;
  pageArticleWithEntry: number = 1;
  pageArticleWithOC: number = 1;
  permissionUpdatable: boolean = false;
  projectName = ProjectName.projectName;
  providerInvoiceDetailContract: ProviderInvoiceDetailModel = new ProviderInvoiceDetailModel();
  providerInvoiceId: number = 0;
  providerInvoiceModel: ProviderInvoiceModel = new ProviderInvoiceModel();
  quantityDetails: number = 0;
  severityState: string = "danger";
  showAccordion: boolean = false;
  stateString: string = 'Abierto';
  subscription: Subscription;
  subscriptionArticleWithEntry: Subscription;
  subscriptionArticleWithOC: Subscription;
  sourceEnums = ProviderInvoiceDetailSourceEnums;
  titleState: string = "Cerrar";
  updateListDetail: number = 0;
  url: string = 'provider-invoice';
  componentTableSelection = CustomTableSelectComponent;
  componentRetentionsModal = ListProviderInvoiceRetentionComponent;
  componentPrefixInvoiceNumberModal = PrefixInvoiceNumberComponent;
  private readonly sourceIdMap: Record<ProviderInvoiceDetailSourceEnums, (row: any) => number> = {
    [ProviderInvoiceDetailSourceEnums.purchaseOrder]: (row: ArticleWithOCTableModel) => row.purchaseOrderDetailId,
    [ProviderInvoiceDetailSourceEnums.inventoryEntry]: (row: ArticleWithEntryTableModel) => row.inventoryEntryDetailId,
    [ProviderInvoiceDetailSourceEnums.others]: (row: ArticleOthersTableModel) => row.articleId,
  };

  async ngOnInit() {
    this.validateForm(this.providerInvoiceModel);
    this.headerService.setTitle(this.nameWindow);
    this.title.setTitle(`${this.nameWindow} - ${this.projectName}`);
    await this.getPermission();
    this.activatedRoute.params.subscribe(async params => {
      this.providerInvoiceId = Object.keys(params).length > 0 ? +params['id'] : 0;
      if (this.providerInvoiceId) {
        await this.getProviderInvoiceById();
      } else {
        await this.getFirstBranchByUserAuthenticated();
      }
    });
    this.subscription = this.outputArticleOthers$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticleOthers(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeArticleOthers(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
    this.subscriptionArticleWithEntry = this.outputArticleWithEntry$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticleWithEntry(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeArticleWithEntry(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
    this.subscriptionArticleWithOC = this.outputArticleWithOC$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChangeArticleWithOC(payload);
          break;
        case 'onFilterChange':
          this.onFilterChangeArticleWithOC(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      const window = windowsOpened.findIndex(x => x === this.url);
      if (window != -1) {
        windowsOpened.splice(window, 1);
        localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      }
    }
  }

  async changeBranch(branchId: number) {
    this.branchId = branchId;
    await this.getAllLists('enterprises');
    this.resetContract();
  }

  async changeDate() {
    const currentDate = this.formProviderInvoice.controls['date'].value;
    const providerId = this.formProviderInvoice.controls['providerId'].value;
    if (providerId && currentDate) {
      const daysExpiration = await firstValueFrom(this.providerInvoiceService.getDaysExpirationByProviderId(providerId));
      const dateExpirationValue = currentDate ? new Date(currentDate) : null;
      if (dateExpirationValue) {
        dateExpirationValue.setDate(dateExpirationValue.getDate() + daysExpiration);
      }
      this.formProviderInvoice.controls['dateExpiration'].setValue(dateExpirationValue);
    }
  }

  async changePrefixInvoiceNumber() {
    const currentBranchId = this.formProviderInvoice.controls['branchId'].value;
    const currentProviderId = this.formProviderInvoice.controls['providerId'].value;
    let currentPrefix = this.formProviderInvoice.controls['prefix'].value;
    let currentInvoiceNumber = this.formProviderInvoice.controls['invoiceNumber'].value;

    if (currentPrefix?.trim() == '') currentPrefix = null;
    if (currentInvoiceNumber?.toString().trim() == '') currentInvoiceNumber = 0;

    if (currentBranchId == this.providerInvoiceModel.branchId &&
      currentProviderId == this.providerInvoiceModel.providerId &&
      currentPrefix == this.providerInvoiceModel.prefix &&
      currentInvoiceNumber == this.providerInvoiceModel.invoiceNumber) return;

    if (currentBranchId == null || currentBranchId <= 0) {
      this.toastHandlerService.showToast('warning', 'Debe seleccionar una sucursal válida.', 'Atención');
      return;
    }
    if (currentProviderId == null || currentProviderId <= 0) {
      this.toastHandlerService.showToast('warning', 'Debe seleccionar un proveedor válido.', 'Atención');
      return;
    }
    if (currentInvoiceNumber == null || currentInvoiceNumber <= 0) {
      this.toastHandlerService.showToast('warning', 'Debe ingresar un número de factura mayor a cero.', 'Atención');
      return;
    }

    try {
      const response = await firstValueFrom(
        this.providerInvoiceService.getProviderInvoiceByKeys(currentBranchId, currentProviderId, currentPrefix, currentInvoiceNumber)
      );

      if (response.providerInvoiceId === 0) {
        const accepted = await this.confirmDialogService.confirm(`¿Factura Proveedor ${response.prefix == null ? '' : response.prefix} ${response.invoiceNumber} no existe. Desea agregarla?`);
        if (accepted) {
          this.providerInvoiceId = 0;
          this.providerInvoiceModel = new ProviderInvoiceModel();
          this.providerInvoiceModel.providerInvoiceId = 0;
          this.providerInvoiceModel.branchId = response.branchId;
          this.providerInvoiceModel.providerId = response.providerId;
          this.providerInvoiceModel.prefix = response.prefix;
          this.providerInvoiceModel.invoiceNumber = response.invoiceNumber;
          this.providerInvoiceModel.ivaTypeId = IvaTypeEnums.pleno;
          this.validateForm(this.providerInvoiceModel);
          this.location.replaceState(`/provider-invoice/register`);
          this.changeDate();
        } else {
          this.resetContract();
        }
      } else {
        if (response.branchId != currentBranchId) {
          this.toastHandlerService.showToast('error', 'La factura proveedor se encuentra en otra sucursal.');
          this.resetContract();
          return;
        }
        this.providerInvoiceModel = response;
        this.providerInvoiceId = this.providerInvoiceModel.providerInvoiceId;
        this.validateForm(this.providerInvoiceModel);
        this.location.replaceState(`/provider-invoice/register/${this.providerInvoiceId}`);
      }
    } catch (error) {
      this.resetContract();
      console.error('Error al buscar la factura proveedor por claves.', error);
    }
  }

  changeProvider(providerId: number) {
    this.providerInvoiceId = 0;
    this.providerInvoiceModel = new ProviderInvoiceModel();
    this.providerInvoiceModel.branchId = this.branchId;
    this.providerInvoiceModel.providerId = providerId;
    this.providerInvoiceModel.ivaTypeId = IvaTypeEnums.pleno;
    this.validateForm(this.providerInvoiceModel);
    this.location.replaceState(`/provider-invoice/register`);
    this.changeDate();
  }

  async changeState() {
    if (this.providerInvoiceId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la factura proveedor.', 'Atención');
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para modificar facturas proveedores.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    try {
      const response = await firstValueFrom(this.providerInvoiceService.changeStateAsync(this.providerInvoiceId));
      this.providerInvoiceModel = response;
      this.validateForm(this.providerInvoiceModel);
      const successMessage = response.state === ProviderInvoiceStateEnum.open ? 'La factura proveedor fue abierta exitosamente.'
        : 'La factura proveedor fue cerrada exitosamente.';
      this.toastHandlerService.showToast('success', successMessage);
    } catch (error) {
      console.error('Error al cambiar el estado de la factura proveedor.', error);
    }
  }

  clicNewDetail(event?: any) {
    if (event) {
      event.stopPropagation();
      event.preventDefault();
      this.isFormView = !this.isFormView;
      if (!this.isFormView) setTimeout(() => { this.isFormView = true }, 100);
    }

    if (this.providerInvoiceModel.providerInvoiceId != 0) {
      this.activePanel = "0";
      this.showAccordion = true;
    } else {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la factura.', 'Atención');
    }
  }

  async createNewProviderInvoice() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'error',
        `El usuario no tiene permisos para crear facturas proveedores. Comuníquese con el administrador del sistema.`
      );
      return;
    }
    this.resetContract();
  }

  async deleteProviderInvoice() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', 'El usuario no tiene permisos para eliminar facturas proveedores. Comuníquese con el administrador del sistema.', '');
      return;
    }
    if (this.providerInvoiceId === 0) {
      this.toastHandlerService.showToast('warning', 'Debe crear el encabezado de la factura proveedor.', 'Atención');
      return;
    }
    const accepted = await this.confirmDialogService.confirm(`¿Realmente desea eliminar la factura proveedor ${this.providerInvoiceModel.prefix} ${this.providerInvoiceModel.invoiceNumber}?`);
    if (accepted) {
      try {
        await firstValueFrom(this.providerInvoiceService.deleteProviderInvoice(this.providerInvoiceId));
        this.toastHandlerService.showToast('success', 'La factura proveedor fue eliminada exitosamente.');
        this.createNewProviderInvoice();
      } catch (error) {
        console.error('Error al eliminar la factura proveedor.', error);
      }
    }
  }

  async getAllLists(...listsToLoad: Array<'branches' | 'providers' | 'enterprises' | 'ivaTypes'>) {
    try {
      const lists = {
        branches: async () => {
          this.listBranch = await firstValueFrom(this.branchService.getAllBranchesForSelectAsync());
        },
        providers: async () => {
          this.listProvider = await firstValueFrom(this.providerService.getAllProvidersForSelect());
        },
        enterprises: async () => {
          this.listEnterprise = await firstValueFrom(this.enterpriseService.getEnterprisesByBranchIdForSelect(this.branchId));
          this.filteredEnterprises = this.listEnterprise;
        },
        ivaTypes: async () => {
          this.listIvaType = await firstValueFrom(this.costCenterService.getIvaTypesForSelect());
        }
      };
      if (listsToLoad.length) {
        await Promise.all(listsToLoad.map(list => lists[list]()));
      } else {
        await Promise.all([
          lists.branches(),
          lists.providers(),
          lists.enterprises(),
          lists.ivaTypes()
        ]);
      }
    } catch (error) {
      console.error('Error al obtener las listas.', error);
    }
  }

  async getButtonClick(buttonClick: any) {
    const branchId = this.formProviderInvoice.get("branchId")?.value;
    let consecutive = this.formProviderInvoice.get("consecutive")?.value;

    if (branchId == null || branchId == 0) {
      this.toastHandlerService.showToast('warning', 'Seleccione una sucursal.', 'Atención');
      return;
    }

    consecutive = consecutive == null ? 0 : consecutive;

    const actionMap: Record<string, () => Promise<ProviderInvoiceModel>> = {
      first: () => firstValueFrom(this.providerInvoiceService.getFirstProviderInvoiceByKeysAsync(branchId)),
      previus: () => firstValueFrom(this.providerInvoiceService.getPreviusProviderInvoiceByKeysAsync(branchId, consecutive)),
      next: () => firstValueFrom(this.providerInvoiceService.getNextProviderInvoiceByKeysAsync(branchId, consecutive)),
      last: () => firstValueFrom(this.providerInvoiceService.getLastProviderInvoiceByKeysAsync(branchId)),
    };
    const action = actionMap[buttonClick.target.id];
    if (!action) {
      this.providerInvoiceId = 0;
      this.providerInvoiceModel = new ProviderInvoiceModel();
      return;
    }
    try {
      const providerInvoice = await action();
      this.providerInvoiceId = providerInvoice.providerInvoiceId;
      this.providerInvoiceModel = providerInvoice;
      this.validateForm(this.providerInvoiceModel);
      this.location.replaceState(`/provider-invoice/register/${this.providerInvoiceId}`);
    } catch (error) {
      this.providerInvoiceModel = new ProviderInvoiceModel();
      this.providerInvoiceModel.providerInvoiceId = 0;
      console.error(`Error al buscar la factura proveedor (${buttonClick.target.id}).`, error);
    }
  }

  async getFirstBranchByUserAuthenticated() {
    try {
      const response = await firstValueFrom(this.branchService.getFirstBranchByUserAuthenticatedAsync());
      await this.changeBranch(response.branchId);
    } catch (error) {
      console.error('Error al obtener la sucursal del usuario.', error);
    }
  }

  async getArticleOthers() {
    try {
      this.dataArticleOthers = await firstValueFrom(
        this.providerInvoiceService.getArticleOthers(
          this.pageArticleOthers,
          this.limitArticleOthers,
          this.columnSortArticleOthers,
          this.ascendantArticleOthers,
          this.filtersArticleOthers
        )
      );
      this.updateInputsArticleOthers();
    } catch (error) {
      console.error('Error al obtener los artículos.', error);
    }
  }

  async getArticleWithEntry() {
    try {
      this.dataArticleWithEntry = await firstValueFrom(
        this.providerInvoiceService.getArticleWithEntry(
          this.providerInvoiceModel.providerId,
          this.providerInvoiceModel.branchId,
          this.pageArticleWithEntry,
          this.limitArticleWithEntry,
          this.columnSortArticleWithEntry,
          this.ascendantArticleWithEntry,
          this.filtersArticleWithEntry
        )
      );
      this.updateInputsArticleWithEntry();
    } catch (error) {
      console.error('Error al obtener los artículos con entrada.', error);
    }
  }

  async getArticleWithOC() {
    try {
      this.dataArticleWithOC = await firstValueFrom(
        this.providerInvoiceService.getArticleWithOC(
          this.providerInvoiceModel.providerId,
          this.providerInvoiceModel.branchId,
          this.pageArticleWithOC,
          this.limitArticleWithOC,
          this.columnSortArticleWithOC,
          this.ascendantArticleWithOC,
          this.filtersArticleWithOC
        )
      );
      this.updateInputsArticleWithOC();
    } catch (error) {
      console.error('Error al obtener los artículos con OC.', error);
    }
  }

  async getColumnsArticleOthers() {
    try {
      if (this.headerArticleOthers.length === 0) {
        this.headerArticleOthers = await firstValueFrom(this.providerInvoiceService.getColumnsForArticleOthers());
      }
    } catch (error) {
      console.error('Error al buscar las columnas de artículos.', error);
    }
  }

  async getColumnsArticleWithEntry() {
    try {
      if (this.headerArticleWithEntry.length === 0) {
        this.headerArticleWithEntry = await firstValueFrom(this.providerInvoiceService.getColumnsForArticleWithEntry());
      }
    } catch (error) {
      console.error('Error al buscar las columnas de artículos con entrada.', error);
    }
  }

  async getColumnsArticleWithOC() {
    try {
      if (this.headerArticleWithOC.length === 0) {
        this.headerArticleWithOC = await firstValueFrom(this.providerInvoiceService.getColumnsForArticleWithOC());
      }
    } catch (error) {
      console.error('Error al buscar las columnas de artículos con OC.', error);
    }
  }

  async getPermission() {
    const windowsOpenedJson = localStorage.getItem(AuthenticationContants.keyWindowsOpenedLocalStorage);
    if (windowsOpenedJson) {
      const windowsOpened: Array<string> = JSON.parse(windowsOpenedJson);
      this.isOpened = windowsOpened.find(x => x === this.url);
      windowsOpened.push(this.url);
      localStorage.setItem(AuthenticationContants.keyWindowsOpenedLocalStorage, JSON.stringify(windowsOpened));
      if (this.isOpened) {
        this.toastHandlerService.showToast('warning', `No se permite tener dos ventanas/pestañas de ${this.nameWindow} abiertas.`, 'Atención');
      }
    } else {
      localStorage.clear();
    }
    try {
      this.permissionUpdatable = await firstValueFrom(this.windowService.getPermissionProfileByUrl(this.url));
    } catch (error) {
      console.error('Error al buscar los permisos del perfil.', error);
    }
    await this.getAllLists();
  }

  async getProviderInvoiceById() {
    try {
      this.providerInvoiceModel = await firstValueFrom(this.providerInvoiceService.getProviderInvoiceById(this.providerInvoiceId));
      this.branchId = this.providerInvoiceModel.branchId;
      this.providerInvoiceId = this.providerInvoiceModel.providerInvoiceId;
      await this.getAllLists('enterprises');
      this.validateForm(this.providerInvoiceModel);
    } catch (error) {
      console.error('Error al buscar la factura proveedor.', error);
    }
  }

  getUpdateProviderInvoice(providerInvoice: ProviderInvoiceModel) {
    this.providerInvoiceModel = providerInvoice;
    this.validateForm(this.providerInvoiceModel);
  }

  goToBack(event?: Event) {
    event?.preventDefault();
    this.router.navigate(['/provider-invoice']);
  }

  goToHome(event?: Event) {
    event?.preventDefault();
    this.router.navigate(['/main']);
  }

  async handleSelectedItems(articles: any[], source: ProviderInvoiceDetailSourceEnums) {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', `No tiene permisos para modificar facturas proveedores.
        Comuníquese con el administrador del sistema.`);
      return;
    }
    if (articles.length === 0) return;
    this.isFormView = !this.isFormView;
    if (articles.length === 1) {
      this.providerInvoiceDetailContract = Object.assign(new ProviderInvoiceDetailModel(), articles[0]);
      const article = new ArticleModel();
      article.name = articles[0].articleName;
      article.code = articles[0].articleCode;
      this.providerInvoiceDetailContract.article = article;
      this.providerInvoiceDetailContract.getRegister = true;
      return;
    }
    const request = Object.assign(new ProviderInvoiceDetailBulkModel(), {
      providerInvoiceId: this.providerInvoiceId,
      source,
      items: articles.map(row => Object.assign(new ProviderInvoiceDetailBulkItemModel(), {
        sourceId: this.sourceIdMap[source](row),
        conceptInvoicingId: row.conceptInvoicingId,
      })),
    });
    try {
      await firstValueFrom(this.providerInvoiceDetailService.createBulkProviderInvoiceDetail(request));
      this.toastHandlerService.showToast('success', `Se agregaron ${articles.length} detalles a la factura proveedor.`);
      this.updateListDetail++;
    } catch (error) {
      console.error('Error al crear los detalles de la factura proveedor.', error);
    }
  }

  async onFilterChangeArticleOthers(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticleOthers = this.removeDuplicates(filters);
    this.filtersArticleOthers.forEach((filter: any) => this.processFilterArticleOthers(filter));
    this.pageArticleOthers = 1;
    await this.getArticleOthers();
  }

  async onPageChangeArticleOthers(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.pageArticleOthers = event.page;
    this.limitArticleOthers = event.pageSize;
    this.columnSortArticleOthers = event.sortField || null;
    this.ascendantArticleOthers = event.sortOrder === 1;
    await this.getArticleOthers();
  }

  async onFilterChangeArticleWithEntry(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticleWithEntry = this.removeDuplicates(filters);
    this.filtersArticleWithEntry.forEach((filter: any) => {
      this.filtersArticleWithEntry = this.processSelectionFilter(this.filtersArticleWithEntry, filter);
    });
    this.pageArticleWithEntry = 1;
    await this.getArticleWithEntry();
  }

  async onFilterChangeArticleWithOC(event: { filters: any }) {
    const filters = event.filters
      .map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index)
      .map((item: any) => item.value);
    this.filtersArticleWithOC = this.removeDuplicates(filters);
    this.filtersArticleWithOC.forEach((filter: any) => {
      this.filtersArticleWithOC = this.processSelectionFilter(this.filtersArticleWithOC, filter);
    });
    this.pageArticleWithOC = 1;
    await this.getArticleWithOC();
  }

  async onPageChangeArticleWithEntry(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.pageArticleWithEntry = event.page;
    this.limitArticleWithEntry = event.pageSize;
    this.columnSortArticleWithEntry = event.sortField || null;
    this.ascendantArticleWithEntry = event.sortOrder === 1;
    await this.getArticleWithEntry();
  }

  async onPageChangeArticleWithOC(event: {
    page: number;
    pageSize: number;
    sortField: string | undefined;
    sortOrder: number;
  }) {
    this.pageArticleWithOC = event.page;
    this.limitArticleWithOC = event.pageSize;
    this.columnSortArticleWithOC = event.sortField || null;
    this.ascendantArticleWithOC = event.sortOrder === 1;
    await this.getArticleWithOC();
  }

  async openPrefixInvoiceNumberModal() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para modificar facturas proveedores, comuniquese con el administrador del sistema.`
      );
      return;
    }
    this.inputsForRetentionsModal$.next({
      providerInvoiceModel: this.providerInvoiceModel,
      permissionUpdatable: this.permissionUpdatable,
      disabledForm: this.disabledForm
    });
    this.prefixInvoiceNumberModalComponent?.show();
  }

  async openRetentionsModal() {
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast(
        'warning',
        `El usuario no tiene permisos para aplicar retenciones, comuniquese con el administrador del sistema.`
      );
      return;
    }
    this.inputsForRetentionsModal$.next({
      providerInvoiceModel: this.providerInvoiceModel,
      permissionUpdatable: this.permissionUpdatable,
      disabledForm: this.disabledForm
    });
    this.retentionsModalComponent?.show();
  }

  async openSelectArticleOthers() {
    if (this.providerInvoiceModel.providerInvoiceId === 0) {
      this.toastHandlerService.showToast('warning', `Debe crear el encabezado de la factura proveedor.`, 'Atención');
      return;
    }
    this.activePanel = "0";
    this.showAccordion = true;
    this.isFormView = !this.isFormView;
    if (!this.isFormView) setTimeout(() => { this.isFormView = true }, 100);
    this.filtersArticleOthers = [];
    this.pageArticleOthers = 1;
    this.providerInvoiceDetailContract.getRegister = false;
    await Promise.all([
      this.getColumnsArticleOthers(),
      this.getArticleOthers(),
    ]);
    this.updateInputsArticleOthers();

    if (this.articleOthersSelectionModalComponent && this.headerArticleOthers) {
      this.articleOthersSelectionModalComponent.show();
    } else {
      this.toastHandlerService.showToast('error', 'No se pudieron cargar los artículos. Por favor, inténtelo de nuevo.');
    }
  }

  async openSelectArticleWithEntry() {
    if (this.providerInvoiceModel.providerInvoiceId === 0) {
      this.toastHandlerService.showToast('warning', `Debe crear el encabezado de la factura proveedor.`, 'Atención');
      return;
    }
    this.activePanel = "0";
    this.showAccordion = true;
    this.isFormView = !this.isFormView;
    if (!this.isFormView) setTimeout(() => { this.isFormView = true }, 100);
    this.filtersArticleWithEntry = [];
    this.pageArticleWithEntry = 1;
    this.providerInvoiceDetailContract.getRegister = false;
    await Promise.all([
      this.getColumnsArticleWithEntry(),
      this.getArticleWithEntry(),
    ]);
    this.updateInputsArticleWithEntry();

    if (this.articleWithEntrySelectionModalComponent && this.headerArticleWithEntry) {
      this.articleWithEntrySelectionModalComponent.show();
    } else {
      this.toastHandlerService.showToast('error', 'No se pudieron cargar los artículos con entrada. Por favor, inténtelo de nuevo.');
    }
  }

  async openSelectArticleWithOC() {
    if (this.providerInvoiceModel.providerInvoiceId === 0) {
      this.toastHandlerService.showToast('warning', `Debe crear el encabezado de la factura proveedor.`, 'Atención');
      return;
    }
    this.activePanel = "0";
    this.showAccordion = true;
    this.isFormView = !this.isFormView;
    if (!this.isFormView) setTimeout(() => { this.isFormView = true }, 100);
    this.filtersArticleWithOC = [];
    this.pageArticleWithOC = 1;
    this.providerInvoiceDetailContract.getRegister = false;
    await Promise.all([
      this.getColumnsArticleWithOC(),
      this.getArticleWithOC(),
    ]);
    this.updateInputsArticleWithOC();

    if (this.articleWithOCSelectionModalComponent && this.headerArticleWithOC) {
      this.articleWithOCSelectionModalComponent.show();
    } else {
      this.toastHandlerService.showToast('error', 'No se pudieron cargar los artículos con OC. Por favor, inténtelo de nuevo.');
    }
  }

  processFilterArticleOthers(filter: any): void {
    this.filtersArticleOthers = this.processSelectionFilter(this.filtersArticleOthers, filter);
  }

  processSelectionFilter(filters: any[], filter: any): any[] {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (
      valueFilter === null ||
      valueFilter === undefined ||
      valueFilter === '' ||
      valueFilter === 0
    ) {
      return filters.filter((f) => f.propertyName !== propertyName);
    }
    const existingFilter = filters.find((f) => f.propertyName === propertyName);
    if (existingFilter) {
      existingFilter.valueFilter = valueFilter;
      existingFilter.valueAlterFilter = valueAlterFilter;
    }
    return filters;
  }

  async reloadProviderInvoice(params: any) {
    if (params.updateProviderInvoice) {
      await this.getProviderInvoiceById();
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) => index === self.findIndex((t: any) => t.propertyName === value.propertyName));
  }

  async resetContract() {
    this.providerInvoiceId = 0;
    this.providerInvoiceModel = new ProviderInvoiceModel();
    this.providerInvoiceModel.branchId = this.branchId;
    this.providerInvoiceModel.ivaTypeId = IvaTypeEnums.pleno;
    this.validateForm(this.providerInvoiceModel);
    this.location.replaceState(`/provider-invoice/register`);
  }

  async send() {
    Object.keys(this.formProviderInvoice.controls).forEach((key) => {
      const control = this.formProviderInvoice.get(key);
      if (control && control.invalid) {
        console.warn(`${key} es inválido`, control.errors, 'value:', control.value);
      }
    });
    if (this.formProviderInvoice.invalid) {
      this.showValidationMessages();
      return;
    }
    if (!this.permissionUpdatable) {
      this.toastHandlerService.showToast('error', 'El usuario no tiene permisos para crear o modificar facturas proveedores. Comuníquese con el administrador del sistema.');
      return;
    }

    const formValue = this.formProviderInvoice.getRawValue() as ProviderInvoiceModel;
    if (formValue.observation?.trim() === '') formValue.observation = null;

    if (this.providerInvoiceId === 0) {
      const newProviderInvoice = Object.assign(new ProviderInvoiceModel(), formValue);
      try {
        const response = await firstValueFrom(this.providerInvoiceService.createProviderInvoice(newProviderInvoice));
        this.toastHandlerService.showToast('success', 'La factura proveedor fue creada exitosamente.', '');
        this.providerInvoiceModel = response;
        this.providerInvoiceId = this.providerInvoiceModel.providerInvoiceId;
        this.validateForm(this.providerInvoiceModel);
        this.location.replaceState(`/provider-invoice/register/${this.providerInvoiceId}`);
        this.activePanel = "0";
        this.showAccordion = true;
        this.isFormView = true;
      } catch (error) {
        console.error('Error al crear la factura proveedor.', error);
      }
    } else {
      Object.assign(this.providerInvoiceModel, formValue);
      try {
        const response = await firstValueFrom(this.providerInvoiceService.updateProviderInvoice(this.providerInvoiceModel));
        this.toastHandlerService.showToast('success', 'La factura proveedor fue modificada exitosamente.', '');
        this.providerInvoiceModel = response;
        this.validateForm(this.providerInvoiceModel);
      } catch (error) {
        console.error('Error al actualizar la factura proveedor.', error);
      }
    }
  }
  showValidationMessages() {
    const controlMessages: Record<string, Record<string, string>> = {
      branchId: {
        required: 'El campo Sucursal es obligatorio.',
        min: 'Debe seleccionar una Sucursal válida.'
      },
      providerId: {
        required: 'El campo Proveedor es obligatorio.',
        min: 'Debe seleccionar un Proveedor válido.'
      },
      prefix: {
        maxlength: 'El campo Prefijo no puede tener más de 6 caracteres.'
      },
      invoiceNumber: {
        required: 'El campo Número Fra. es obligatorio.',
        min: 'Debe ingresar un Número Fra. superior a 0.'
      },
      consecutive: {
        required: 'El campo Consecutivo es obligatorio.'
      },
      enterpriseId: {
        required: 'El campo Empresa es obligatorio.',
        min: 'Debe seleccionar una Empresa válida.'
      },
      date: {
        required: 'El campo Fecha es obligatorio.'
      },
      dateExpiration: {
        required: 'El campo Fecha Vencimiento es obligatorio.'
      },
      ivaTypeId: {
        required: 'El campo Tipo IVA es obligatorio.',
        min: 'Debe seleccionar un Tipo IVA válido.'
      },
      observation: {
        maxlength: 'El campo Observación no puede tener más de 90 caracteres.'
      }
    };

    Object.keys(controlMessages).forEach(control => {
      const formControl = this.formProviderInvoice.get(control);
      if (formControl && formControl.invalid && formControl.errors) {
        const messages = controlMessages[control];
        Object.keys(formControl.errors).forEach(errorKey => {
          const message = messages[errorKey];
          if (message) {
            this.toastHandlerService.showToast('warning', message, 'Atención');
          }
        });
      }
    });
  }

  toggleAccordion() {
    this.showAccordion = !this.showAccordion;
    this.isFormView = false;
  }

  updateFormState() {
    if (!this.permissionUpdatable) {
      this.formProviderInvoice.disable();
    }
    if (this.providerInvoiceId !== 0) {
      this.isFormView = true;
    }
    const currentState = PROVIDER_INVOICE_STATE_CONFIG[this.providerInvoiceModel.state]
      ?? PROVIDER_INVOICE_STATE_CONFIG[ProviderInvoiceStateEnum.open];
    this.stateString = currentState.label;
    this.severityState = currentState.severity;
    this.titleState = currentState.titleState;
    this.iconState = currentState.iconState;
    if (this.providerInvoiceModel.state !== ProviderInvoiceStateEnum.open) {
      this.formProviderInvoice.disable();
      this.formControlStateService.setControlState(this.formProviderInvoice, ['providerId', 'prefix', 'invoiceNumber'], true);
      this.disabledForm = true;
    } else {
      this.formProviderInvoice.enable();
      this.disabledForm = false;
    }
    this.formControlStateService.setControlState(this.formProviderInvoice, ['consecutive'], false);
    this.formProviderInvoiceFooter.disable();
    this.updateListDetail++;
  }

  updateInputsArticleOthers() {
    this.inputsForArticleOthersSelection$.next({
      headers: this.headerArticleOthers,
      data: this.dataArticleOthers,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersArticleOthers,
    });
  }

  updateInputsArticleWithEntry() {
    this.inputsForArticleWithEntrySelection$.next({
      headers: this.headerArticleWithEntry,
      data: this.dataArticleWithEntry,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersArticleWithEntry,
    });
  }

  updateInputsArticleWithOC() {
    this.inputsForArticleWithOCSelection$.next({
      headers: this.headerArticleWithOC,
      data: this.dataArticleWithOC,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      filters: this.filtersArticleWithOC,
    });
  }

  validateForm(providerInvoice: ProviderInvoiceModel) {
    this.formProviderInvoice = this.formBuilder.group({
      branchId: [providerInvoice.branchId, [Validators.required, Validators.min(1)]],
      providerId: [providerInvoice.providerId, [Validators.required, Validators.min(1)]],
      prefix: [providerInvoice.prefix, [Validators.maxLength(6)]],
      consecutive: [providerInvoice.consecutive, [Validators.required]],
      invoiceNumber: [providerInvoice.invoiceNumber, [Validators.required, Validators.min(1)]],
      date: [providerInvoice.date ? this.dateConverterService.convertDatePreserveDay(providerInvoice.date) : null, [Validators.required]],
      dateExpiration: [providerInvoice.dateExpiration ? this.dateConverterService.convertDatePreserveDay(providerInvoice.dateExpiration) : null, [Validators.required]],
      observation: [providerInvoice.observation, [Validators.maxLength(90)]],
      enterpriseId: [providerInvoice.enterpriseId, [Validators.required, Validators.min(1)]],
      ivaTypeId: [providerInvoice.ivaTypeId, [Validators.required, Validators.min(1)]],
    });

    this.formProviderInvoiceFooter = this.formBuilder.group({
      noteDebitValue: [providerInvoice.noteDebitValue, [Validators.min(0), Validators.max(99999999999.99)]],
      noteCreditValue: [providerInvoice.noteCreditValue, [Validators.min(0), Validators.max(99999999999.99)]],
      paymentValue: [providerInvoice.paymentValue, [Validators.min(0), Validators.max(99999999999.99)]],
      residueValue: [providerInvoice.residueValue, [Validators.min(0), Validators.max(99999999999.99)]],
      subtotalValue: [providerInvoice.subtotalValue, [Validators.min(0), Validators.max(99999999999.99)]],
      ivaValue: [providerInvoice.ivaValue, [Validators.min(0), Validators.max(99999999999.99)]],
      totalValue: [(providerInvoice.subtotalValue + providerInvoice.ivaValue), [Validators.min(0), Validators.max(99999999999.99)]],
      retentionValue: [providerInvoice.retentionValue, [Validators.min(0), Validators.max(99999999999.99)]],
      advanceValue: [providerInvoice.advanceValue, [Validators.min(0), Validators.max(99999999999.99)]],
      retainedValue: [providerInvoice.retainedValue, [Validators.min(0), Validators.max(99999999999.99)]],
      valueToPay: [(providerInvoice.subtotalValue + providerInvoice.ivaValue), [Validators.min(0), Validators.max(99999999999.99)]]
    });
    this.updateFormState();
  }

}
